import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";

import { MdsPanelContainer } from "@/design-system/components/panel";
import { useAppStore } from "@/store";
import {
  AudioRecordingCoordinator,
  type AudioRecordingSnapshot,
} from "@/store/audio-capture/coordinator";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextVariant, MDSTextColor } from "@/mds";
import { RecordingControls, RecordingLevelMeter, RecordingPlayback } from "./components";
import { useAudioAudioRecording, useMicrophoneLevelMonitor } from "./hooks";
import { DefaultUploadManager } from "@/store/audio-capture/processing/upload/upload-manager";
import type { TranscriptArrayItem } from "@/store/audio-capture/transcription/utils/types";

export const AudioRecordingDetailPage: FC = observer(() => {
  const { recordingId } = useParams<{ recordingId: string }>();
  const { store } = useAppStore();
  const coordinator = useMemo(
    () => new AudioRecordingCoordinator(store.memDb.audioRecordings),
    [store.memDb.audioRecordings]
  );
  const [snapshot, setSnapshot] = useState<AudioRecordingSnapshot | null>(null);
  const recordingRun = useAudioAudioRecording();
  const microphoneMonitor = useMicrophoneLevelMonitor();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [localUploadPercent, setLocalUploadPercent] = useState<number | null>(null);
  const uploadManagerRef = useRef<DefaultUploadManager | null>(null);
  const [isUploadPaused, setIsUploadPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!recordingId) return;

    const load = async () => {
      const result = await coordinator.loadRecording({ recordingId: recordingId });
      if (!cancelled) {
        setSnapshot(result);
        if (result?.metadata.upload.progressPercent != null) {
          setLocalUploadPercent(result.metadata.upload.progressPercent);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [coordinator, recordingId, recordingRun.status]);

  const isCompleted = snapshot?.metadata.status === "STOPPED";
  const canControl = !!snapshot && !isCompleted;
  const uploadState = snapshot?.metadata.upload?.state ?? "NOT_STARTED";

  const handleUpload = useCallback(async () => {
    if (!snapshot) return;
    const recordingId = snapshot.metadata.recordingId;
    if (!snapshot.audioBlobs.length) {
      setUploadError("No audio data to upload");
      return;
    }

    setUploading(true);
    setUploadError(null);
    setIsUploadPaused(false);

    const manager = new DefaultUploadManager({
      minUploadDurationMs: 1000,
      progressUpdateIntervalMs: 50,
    });
    uploadManagerRef.current = manager;

    const audioBlob = new Blob(
      snapshot.audioBlobs.map(entry => entry.blobData),
      {
        type: snapshot.audioBlobs[0]?.blobData.type || "audio/webm",
      }
    );

    const transcriptItems: TranscriptArrayItem[] = [];
    snapshot.channels.forEach(channelData => {
      const sortedChunks = [...channelData.transcriptChunks].sort((a, b) => {
        const left = a.timestamp ?? a.turn_order ?? 0;
        const right = b.timestamp ?? b.turn_order ?? 0;
        return left - right;
      });
      sortedChunks.forEach(chunk => {
        transcriptItems.push({
          sort_key: `${channelData.channel}-${chunk.timestamp ?? chunk.turn_order ?? 0}`,
          transcript: chunk.transcript ?? "",
          channel: channelData.channel,
        });
      });
    });
    transcriptItems.sort((a, b) => a.sort_key.localeCompare(b.sort_key));
    const transcriptText = transcriptItems.map(item => item.transcript).join("\n");

    await coordinator.updateUploadState({
      recordingId,
      update: {
        state: "IN_PROGRESS",
        progressPercent: 0,
      },
    });
    setLocalUploadPercent(0);

    try {
      const result = await manager.start(
        audioBlob,
        {
          capturedAudioId: snapshot.metadata.capturedAudioId,
          transcriptText,
          transcriptArray: transcriptItems,
          preferredMimeType: audioBlob.type,
        },
        {
          onProgress: percent => {
            const rounded = Math.floor(percent);
            setLocalUploadPercent(rounded);
            coordinator.updateUploadState({
              recordingId,
              update: {
                state: "IN_PROGRESS",
                progressPercent: rounded,
              },
            });
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      await coordinator.updateUploadState({
        recordingId,
        update: {
          state: "COMPLETED",
          progressPercent: 100,
        },
      });
      setLocalUploadPercent(100);
      const refreshed = await coordinator.loadRecording({ recordingId });
      setSnapshot(refreshed);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to upload";
      setUploadError(message);
      await coordinator.updateUploadState({
        recordingId,
        update: {
          state: "FAILED",
          error: message,
          progressPercent: localUploadPercent ?? 0,
        },
      });
    } finally {
      setUploading(false);
      setIsUploadPaused(false);
      if (uploadManagerRef.current === manager) {
        uploadManagerRef.current = null;
      }
    }
  }, [coordinator, snapshot, localUploadPercent]);

  const handlePauseUpload = useCallback(() => {
    uploadManagerRef.current?.pause();
    setIsUploadPaused(true);
    if (snapshot) {
      coordinator.updateUploadState({
        recordingId: snapshot.metadata.recordingId,
        update: {
          state: "IN_PROGRESS",
          progressPercent: localUploadPercent ?? 0,
        },
      });
    }
  }, [coordinator, snapshot, localUploadPercent]);

  const handleResumeUpload = useCallback(() => {
    uploadManagerRef.current?.resume();
    setIsUploadPaused(false);
    if (snapshot) {
      coordinator.updateUploadState({
        recordingId: snapshot.metadata.recordingId,
        update: {
          state: "IN_PROGRESS",
          progressPercent: localUploadPercent ?? 0,
        },
      });
    }
  }, [coordinator, snapshot, localUploadPercent]);

  const handleCancelUpload = useCallback(async () => {
    if (!snapshot) return;
    const recordingId = snapshot.metadata.recordingId;
    uploadManagerRef.current?.cancel();
    uploadManagerRef.current = null;
    setUploading(false);
    setIsUploadPaused(false);
    await coordinator.updateUploadState({
      recordingId,
      update: {
        state: "FAILED",
        error: "Upload cancelled",
        progressPercent: localUploadPercent ?? 0,
      },
    });
  }, [coordinator, snapshot, localUploadPercent]);

  useEffect(() => {
    return () => {
      uploadManagerRef.current?.cancel();
      uploadManagerRef.current = null;
    };
  }, []);

  const handleDownload = useCallback(() => {
    if (!snapshot?.audioBlobs.length) return;
    const blob = new Blob(
      snapshot.audioBlobs.map(entry => entry.blobData),
      {
        type: snapshot.audioBlobs[0]?.blobData.type || "audio/webm",
      }
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${snapshot.metadata.recordingId}.webm`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [snapshot]);

  return (
    <MdsPanelContainer>
      <h1>Audio Recording</h1>
      {snapshot ? (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <MDSText variant={MDSTextVariant.TextSm}>Run ID: {snapshot.metadata.recordingId}</MDSText>
          {snapshot.metadata.capturedAudioId && (
            <MDSText variant={MDSTextVariant.TextSm}>
              Captured Audio ID: {snapshot.metadata.capturedAudioId}
            </MDSText>
          )}
          {snapshot.metadata.noteId && (
            <MDSText variant={MDSTextVariant.TextSm}>Note ID: {snapshot.metadata.noteId}</MDSText>
          )}
          <MDSText variant={MDSTextVariant.TextSm}>
            Created: {new Date(snapshot.metadata.createdAt).toLocaleString()}
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>Status: {snapshot.metadata.status}</MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>
            Total Captured (ms): {snapshot.metadata.totalCapturedMs}
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>
            Upload State: {snapshot.metadata.upload.state}
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>
            Transform State: {snapshot.metadata.transform.state}
          </MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>Channels: {snapshot.channels.length}</MDSText>
          <MDSText variant={MDSTextVariant.TextSm}>
            Audio Blobs: {snapshot.audioBlobs.length}
          </MDSText>

          {canControl && (
            <section style={{ display: "grid", gap: "0.5rem" }}>
              <RecordingControls
                status={recordingRun.status}
                onStart={() =>
                  recordingRun.start({
                    recordingId: snapshot.metadata.recordingId,
                    noteId: snapshot.metadata.noteId,
                    capturedAudioId: snapshot.metadata.capturedAudioId,
                  })
                }
                onPause={recordingRun.pause}
                onResume={recordingRun.resume}
                onStop={recordingRun.stop}
                isDisabled={uploading}
                hasExistingData={(snapshot.metadata.totalCapturedMs ?? 0) > 0}
              />
              <MDSText variant={MDSTextVariant.TextSm}>
                Elapsed: {(recordingRun.elapsedMs / 1000).toFixed(1)}s
              </MDSText>
              <RecordingLevelMeter
                level={
                  recordingRun.status === "RECORDING" || recordingRun.status === "PAUSED"
                    ? recordingRun.level
                    : microphoneMonitor.level
                }
              />
              {recordingRun.error && (
                <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Danger}>
                  {recordingRun.error}
                </MDSText>
              )}
            </section>
          )}
          {recordingRun.status === "IDLE" && microphoneMonitor.error && (
            <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Danger}>
              {microphoneMonitor.error}
            </MDSText>
          )}
          <section style={{ display: "grid", gap: "0.5rem" }}>
            <MDSText variant={MDSTextVariant.TextSm}>
              Upload State: {uploadState}
              {localUploadPercent != null && ` (${localUploadPercent}%)`}
            </MDSText>
            {uploadState !== "COMPLETED" ? (
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {!uploading && (
                  <MDSButton onClick={handleUpload}>
                    {uploadState === "FAILED" ? "Retry Upload" : "Upload Recording"}
                  </MDSButton>
                )}
                {uploading && (
                  <>
                    {isUploadPaused ? (
                      <MDSButton onClick={handleResumeUpload}>Resume Upload</MDSButton>
                    ) : (
                      <MDSButton onClick={handlePauseUpload}>Pause Upload</MDSButton>
                    )}
                    <MDSButton variant={MDSButtonVariant.Subtle} onClick={handleCancelUpload}>
                      Cancel Upload
                    </MDSButton>
                  </>
                )}
              </div>
            ) : (
              <MDSButton onClick={handleDownload}>Download Audio</MDSButton>
            )}
            {uploadError && (
              <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Danger}>
                {uploadError}
              </MDSText>
            )}
          </section>
          {isCompleted && (
            <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Secondary}>
              Recording stopped
            </MDSText>
          )}

          <RecordingPlayback snapshot={snapshot} />
        </div>
      ) : (
        <MDSText variant={MDSTextVariant.TextSm}>Recording not found.</MDSText>
      )}
    </MdsPanelContainer>
  );
});

export default AudioRecordingDetailPage;
